import { FormPepsAval, FormPepsConyuge } from "@interfaces/catalogs";
import { FormikErrors, FormikProps, FormikTouched } from "formik";
import { useCallback } from "react";

/**
 * Hook para validar campos de un formulario usando Formik
 * @param options - Opciones del hook
 * @returns Función para validar campos
 *
 * @example
 *
 * const formik = useFormik({
 *   initialValues: {
 *     name: "",
 *     email: "",
 *   },
 *   validationSchema: yup.object({
 *     name: yup.string().required("Nombre requerido"),
 *     email: yup.string().email("Email inválido").required("Email requerido"),
 *   }),
 * });
 * const hasErrorInMultipleFields = useFormikErrors({ formik });
 * const isValid = hasErrorInMultipleFields(["name", "email"]);
 * console.log(isValid); // true o false
 */

interface useFormikErrorsOptions<T> {
  formik: FormikProps<T>;
}

const useFormikErrors = <T extends object>(options: useFormikErrorsOptions<T>) => {
  const { formik } = options;

  const validateMultipleFields = (fieldsToValidate: (keyof T)[]): boolean => {
    const { errors } = formik;

    // Si no hay campos para validar, retornar true
    if (!fieldsToValidate || fieldsToValidate.length === 0) {
      return true;
    }

    // Verificar si alguno de los campos tiene errores
    const hasErrors = fieldsToValidate.some((field) => {
      const fieldError = errors[field];
      return fieldError !== undefined && fieldError !== null && fieldError !== "";
    });

    // Retornar false si hay errores, true si todos los campos son válidos
    return !hasErrors;
  };

  const getErrorField = useCallback(
    (fieldName: keyof T): string | undefined => {
      const touched = formik.touched[fieldName];
      if (!touched) {
        return undefined;
      }
      const fieldError = formik.errors[fieldName];
      if (!fieldError) {
        return undefined;
      }

      return fieldError as string;
    },
    [formik.touched, formik.errors],
  );

  const getErrorFieldArray = useCallback(
    (fieldName: keyof T, index: number, inputName: string): string | undefined => {
      const touched = formik.touched[fieldName] as Array<Partial<Record<string, string>>>;
      const errors = formik.errors[fieldName] as Array<Partial<Record<string, string>>>;

      if (touched && touched[index] && errors && errors[index]) {
        if (touched[index][inputName] && errors[index][inputName]) {
          return errors[index][inputName];
        }
      }

      return undefined;
    },
    [formik.touched, formik.errors],
  );

  const getErrorPepsItem = (index: number, fieldName: keyof FormPepsConyuge) => {
    const formik2 = formik as unknown as FormikProps<FormPepsAval>;
    const touched = formik2.touched.peps_conyuge?.[index] as FormikTouched<FormPepsConyuge>;
    const errors = formik2.errors.peps_conyuge?.[index] as FormikErrors<FormPepsConyuge>;

    if (touched && touched[fieldName] && errors && errors[fieldName]) {
      return errors[fieldName];
    }
  };

  return { validateMultipleFields, getErrorField, getErrorFieldArray, getErrorPepsItem };
};

export default useFormikErrors;
